import { useState, useRef, useCallback } from 'react';
import { usePageTracking } from '../../../hooks/usePageTracking';
import ImageUploader from '@/components/base/ImageUploader';

export interface GalleryItem {
  id: string;
  src: string;
  type: 'image' | 'video';
  category: string;
  title: string;
}

const CATEGORIES = ['All', 'Awards', 'Behind the Scenes', 'Media', 'Events', 'Products', 'Services'];
const CATEGORY_DISPLAY: Record<string, string> = {
  'Behind the Scenes': 'BTS',
};

function getYouTubeVideoId(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

function getVimeoVideoId(url: string): string | null {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : null;
}

function getEmbedUrl(src: string, type: 'image' | 'video'): string {
  if (type === 'image') return src;
  const ytId = getYouTubeVideoId(src);
  if (ytId) return `https://www.youtube.com/embed/${ytId}`;
  const vimeoId = getVimeoVideoId(src);
  if (vimeoId) return `https://player.vimeo.com/video/${vimeoId}`;
  return src;
}

function parseGalleryItems(content: string[]): GalleryItem[] {
  if (!Array.isArray(content)) return [];
  return content.map((item, idx) => {
    try {
      if (typeof item === 'object' && item !== null) {
        const i = item as any;
        return {
          id: String(i.id || `item-${idx}`),
          src: String(i.src || ''),
          type: i.type === 'video' ? 'video' : 'image',
          category: String(i.category || 'Services'),
          title: String(i.title || ''),
        };
      }
      const parsed = JSON.parse(item);
      if (parsed && typeof parsed === 'object') {
        return {
          id: String(parsed.id || `item-${idx}`),
          src: String(parsed.src || ''),
          type: parsed.type === 'video' ? 'video' : 'image',
          category: String(parsed.category || 'Services'),
          title: String(parsed.title || ''),
        };
      }
    } catch { /* noop */ }
    // Backward compat: plain URL string treated as image
    return {
      id: `legacy-${idx}`,
      src: String(item || ''),
      type: 'image',
      category: 'Services',
      title: '',
    };
  });
}

function stringifyGalleryItems(items: GalleryItem[]): string[] {
  return items.map(i => JSON.stringify(i));
}

interface GalleryEditorProps {
  content: string[];
  editing: boolean;
  onChange: (newContent: string[]) => void;
  validateImageFile?: (file: File) => boolean;
}

export default function GalleryEditor({ content, editing, onChange, validateImageFile }: GalleryEditorProps) {
  const [activeFilter, setActiveFilter] = useState('All');
  usePageTracking({
    moduleName: 'Website Builder',
    submoduleName: `Content / Gallery / ${activeFilter}`,
    routePath: `/website-builder#content/gallery/${activeFilter.toLowerCase()}`,
  });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addTab, setAddTab] = useState<'image' | 'video'>('image');
  const [newCategory, setNewCategory] = useState('Services');
  const [newTitle, setNewTitle] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoError, setVideoError] = useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const items = parseGalleryItems(content);

  const filteredItems = activeFilter === 'All'
    ? items
    : items.filter(i => i.category === activeFilter);

  const updateItemCategory = useCallback((itemId: string, category: string) => {
    const next = items.map(i => i.id === itemId ? { ...i, category } : i);
    onChange(stringifyGalleryItems(next));
  }, [items, onChange]);

  const updateItemTitle = useCallback((itemId: string, title: string) => {
    const next = items.map(i => i.id === itemId ? { ...i, title } : i);
    onChange(stringifyGalleryItems(next));
  }, [items, onChange]);

  const deleteItem = useCallback((itemId: string) => {
    const next = items.filter(i => i.id !== itemId);
    onChange(stringifyGalleryItems(next));
  }, [items, onChange]);

  const handleFileSelect = (file: File, dataUrl: string) => {
    setPreviewFile(file);
    setPreviewImage(dataUrl);
  };

  const handleItemImageUpload = (itemId: string, file: File, dataUrl: string) => {
    const next = items.map(i => i.id === itemId ? { ...i, src: dataUrl } : i);
    onChange(stringifyGalleryItems(next));
  };

  const validateVideoUrl = (url: string): boolean => {
    if (!url.trim()) return false;
    const ytId = getYouTubeVideoId(url);
    const vimeoId = getVimeoVideoId(url);
    if (!ytId && !vimeoId) {
      setVideoError('Enter a valid YouTube or Vimeo URL');
      return false;
    }
    setVideoError('');
    return true;
  };

  const handleAddAsset = () => {
    if (addTab === 'image') {
      if (!previewImage || !previewFile) return;
      const newItem: GalleryItem = {
        id: `img-${Date.now()}`,
        src: previewImage,
        type: 'image',
        category: newCategory,
        title: newTitle.trim(),
      };
      const next = [...items, newItem];
      onChange(stringifyGalleryItems(next));
    } else {
      if (!validateVideoUrl(videoUrl)) return;
      const newItem: GalleryItem = {
        id: `vid-${Date.now()}`,
        src: videoUrl.trim(),
        type: 'video',
        category: newCategory,
        title: newTitle.trim(),
      };
      const next = [...items, newItem];
      onChange(stringifyGalleryItems(next));
    }
    // Reset modal state
    setAddModalOpen(false);
    setPreviewImage(null);
    setPreviewFile(null);
    setVideoUrl('');
    setVideoError('');
    setNewTitle('');
    setNewCategory('Services');
    setAddTab('image');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const categoryCounts = CATEGORIES.slice(1).map(cat => ({
    name: cat,
    count: items.filter(i => i.category === cat).length,
  }));

  const canAddImage = previewImage !== null;
  const canAddVideo = videoUrl.trim().length > 0 && !videoError;

  return (
    <div className="space-y-4">
      {/* Filter Bar */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {CATEGORIES.map(cat => {
          const count = cat === 'All' ? items.length : items.filter(i => i.category === cat).length;
          const isActive = activeFilter === cat;
          return (
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                isActive
                  ? 'bg-[#0859b8] text-white border-[#0859b8]'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-[#e8f9fb] hover:text-[#0b7f8f] hover:border-[#e8f9fb]'
              }`}
            >
              {CATEGORY_DISPLAY[cat] || cat}
              <span className={`ml-1.5 text-[10px] ${isActive ? 'text-white/70' : 'text-gray-400'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Gallery Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {filteredItems.map((item) => (
          <div
            key={item.id}
            className="group bg-gray-100 rounded-lg overflow-hidden relative aspect-[4/3]"
          >
            {item.type === 'image' ? (
              item.src && (item.src.startsWith('http') || item.src.startsWith('data:') || item.src.startsWith('/')) ? (
                <img
                  src={item.src}
                  alt={item.title || 'Gallery image'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className={`w-full h-full flex flex-col items-center justify-center bg-gray-50 text-gray-300 gap-2 transition-colors`}>
                  <i className="ri-image-line text-3xl"></i>
                  {editing && <span className="text-[10px] text-center px-2">Use 'Asset Details' below to add image</span>}
                </div>
              )
            ) : (
              <iframe
                src={getEmbedUrl(item.src, 'video')}
                title={item.title || 'Video'}
                className="w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}

            {/* Category badge overlay */}
            <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-black/60 text-white backdrop-blur-sm">
              {CATEGORY_DISPLAY[item.category] || item.category}
            </span>

            {/* Title overlay */}
            {item.title && (
              <span className="absolute bottom-2 left-2 right-2 text-[11px] px-2 py-0.5 rounded bg-black/50 text-white truncate">
                {item.title}
              </span>
            )}

            {/* Change Image button removed to standardize upload controls */}
            {/* Delete button (edit mode only) */}
            {editing && (
              <button
                onClick={() => deleteItem(item.id)}
                className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-white/90 text-red-500 hover:bg-red-50 transition-colors cursor-pointer opacity-0 group-hover:opacity-100 shadow-sm"
                title="Remove asset"
              >
                <i className="ri-delete-bin-line text-xs"></i>
              </button>
            )}

            {/* Type badge */}
            {item.type === 'video' && (
              <span className="absolute bottom-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-black/60 text-white">
                <i className="ri-play-fill text-xs"></i>
              </span>
            )}
          </div>
        ))}

        {/* Add Asset button (edit mode) */}
        {editing && (
          <button
            onClick={() => setAddModalOpen(true)}
            className="aspect-[4/3] border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center gap-1.5 cursor-pointer hover:border-gold hover:bg-gold/5 transition-colors"
          >
            <i className="ri-add-line text-gray-400 text-xl"></i>
            <span className="text-[11px] text-gray-400">Add Asset</span>
          </button>
        )}
      </div>

      {/* Item count & category breakdown */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>
          {filteredItems.length} of {items.length} assets shown
          {activeFilter !== 'All' && ` · Filtered by ${activeFilter}`}
        </span>
        <div className="flex items-center gap-3">
          {categoryCounts.filter(c => c.count > 0).map(c => (
            <span key={c.name} className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-gold"></span>
              {CATEGORY_DISPLAY[c.name] || c.name}: {c.count}
            </span>
          ))}
        </div>
      </div>

      {/* Inline category editors (edit mode, below grid) */}
      {editing && items.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2">
          <h4 className="text-xs font-semibold text-gray-700 mb-2">Asset Details</h4>
          {items.map(item => (
            <div key={item.id} className="py-2 border-b border-gray-100 last:border-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-gray-100 flex-shrink-0 overflow-hidden">
                  {item.type === 'image' ? (
                    item.src ? <img src={item.src} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center bg-gray-50 text-gray-300"><i className="ri-image-line text-xs"></i></div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-800">
                      <i className="ri-play-fill text-white text-xs"></i>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0 grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={item.title}
                    onChange={(e) => updateItemTitle(item.id, e.target.value)}
                    placeholder="Caption / Title"
                    className="text-xs text-gray-700 rounded px-2 py-1 border border-gray-200 focus:border-gold focus:outline-none"
                    maxLength={60}
                  />
                  <select
                    value={item.category}
                    onChange={(e) => updateItemCategory(item.id, e.target.value)}
                    className="text-xs text-gray-700 rounded px-2 py-1 border border-gray-200 focus:border-gold focus:outline-none bg-white"
                  >
                    {CATEGORIES.slice(1).map(cat => (
                      <option key={cat} value={cat}>{CATEGORY_DISPLAY[cat] || cat}</option>
                    ))}
                  </select>
                </div>
                <span className="text-[10px] text-gray-400 flex-shrink-0">
                  {item.type === 'video' ? 'Video' : 'Image'}
                </span>
              </div>
              {item.type === 'image' && (
                <div className="mt-3">
                  <ImageUploader 
                    type="gallery" 
                    currentImage={item.src} 
                    onUpload={(file, url) => handleItemImageUpload(item.id, file, url)} 
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Asset Modal */}
      {addModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg w-full max-w-md mx-4 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900">Add Gallery Asset</h3>
              <button
                onClick={() => {
                  setAddModalOpen(false);
                  setPreviewImage(null);
                  setPreviewFile(null);
                  setVideoUrl('');
                  setVideoError('');
                  setNewTitle('');
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 transition-colors cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex rounded-lg bg-gray-100 p-1 mb-4">
              <button
                onClick={() => { setAddTab('image'); setVideoError(''); }}
                className={`flex-1 text-xs py-1.5 rounded-md transition-colors cursor-pointer ${
                  addTab === 'image' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className="ri-image-line mr-1"></i>
                Upload Image
              </button>
              <button
                onClick={() => { setAddTab('video'); setVideoError(''); }}
                className={`flex-1 text-xs py-1.5 rounded-md transition-colors cursor-pointer ${
                  addTab === 'video' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className="ri-video-line mr-1"></i>
                Add Video
              </button>
            </div>

            {/* Category selector */}
            <div className="mb-4">
              <label className="block text-xs text-gray-500 mb-1.5">Category Tag</label>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.slice(1).map(cat => (
                  <button
                    key={cat}
                    onClick={() => setNewCategory(cat)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                      newCategory === cat
                        ? 'bg-[#0859b8] text-white border-[#0859b8]'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-[#e8f9fb] hover:text-[#0b7f8f] hover:border-[#e8f9fb]'
                    }`}
                  >
                    {CATEGORY_DISPLAY[cat] || cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Title input */}
            <div className="mb-4">
              <label className="block text-xs text-gray-500 mb-1">Caption / Title (optional)</label>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g., Team brainstorming session"
                className="w-full text-xs text-gray-700 rounded-lg px-3 py-2 border border-gray-300 focus:border-gold focus:outline-none"
                maxLength={60}
              />
            </div>

            {addTab === 'image' ? (
              <div className="space-y-3">
                {previewImage && (
                  <div className="mb-4">
                    <img src={previewImage} alt="Preview" className="max-h-32 mx-auto rounded border" />
                  </div>
                )}
                <ImageUploader 
                  type="gallery" 
                  currentImage={previewImage} 
                  onUpload={handleFileSelect} 
                />
                {previewImage && (
                  <button
                    onClick={() => {
                      setPreviewImage(null);
                      setPreviewFile(null);
                    }}
                    className="text-xs text-red-500 hover:text-red-600 cursor-pointer block text-center w-full"
                  >
                    Remove selected image
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Video URL</label>
                  <input
                    type="url"
                    value={videoUrl}
                    onChange={(e) => {
                      setVideoUrl(e.target.value);
                      setVideoError('');
                    }}
                    onBlur={() => {
                      if (videoUrl.trim()) validateVideoUrl(videoUrl);
                    }}
                    placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                    className={`w-full text-xs text-gray-700 rounded-lg px-3 py-2 border focus:outline-none ${
                      videoError ? 'border-red-400 bg-red-50/30' : 'border-gray-300 focus:border-gold'
                    }`}
                  />
                  {videoError && (
                    <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>
                      {videoError}
                    </p>
                  )}
                  <p className="text-[10px] text-gray-400 mt-1">
                    Supports YouTube and Vimeo URLs
                  </p>
                </div>
                {/* Video preview */}
                {canAddVideo && (
                  <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                    <iframe
                      src={getEmbedUrl(videoUrl, 'video')}
                      title="Video preview"
                      className="w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 mt-5 pt-3 border-t border-gray-100">
              <button
                onClick={() => {
                  setAddModalOpen(false);
                  setPreviewImage(null);
                  setPreviewFile(null);
                  setVideoUrl('');
                  setVideoError('');
                  setNewTitle('');
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="text-xs text-gray-500 hover:text-gray-700 px-3 py-2 rounded cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAsset}
                disabled={addTab === 'image' ? !canAddImage : !canAddVideo}
                className="text-xs font-medium px-4 py-2 rounded bg-gold text-white hover:bg-gold/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                Add to Gallery
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
